import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, CalendarClock, Target, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, PriorityDot, PersonAvatar } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import { api, errMsg, fmtDate, localToday } from "@/lib/api";
import { useEmployees } from "@/lib/useEmployees";

const COLS = [
  { id: "todo", label: "To do" },
  { id: "in_progress", label: "In progress" },
  { id: "done", label: "Done" },
];
const EMPTY = { title: "", description: "", status: "todo", priority: "medium", assignee_id: "", due_date: "", goal_id: "" };

function TaskDialog({ open, onOpenChange, initial, employees, goals, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial, assignee_id: initial.assignee_id || "", goal_id: initial.goal_id || "", due_date: initial.due_date || "" } : EMPTY); }, [open, initial]);
  const editing = !!initial?.task_id;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { title: f.title, description: f.description, status: f.status, priority: f.priority, assignee_id: f.assignee_id || "", due_date: f.due_date || null, goal_id: f.goal_id || "" };
    try {
      if (editing) await api.patch(`/tasks/${initial.task_id}`, body);
      else await api.post("/tasks", { ...body, assignee_id: f.assignee_id || null, goal_id: f.goal_id || null });
      toast.success(editing ? "Task updated" : "Task created");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/tasks/${initial.task_id}`); toast.success("Task deleted"); onOpenChange(false); onSaved(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>Title</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title-input" /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="task-description-input" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Assignee</Label>
              <Select value={f.assignee_id || "none"} onValueChange={(v) => setF({ ...f, assignee_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Due date</Label><DatePicker value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} allowClear testid="task-due-date-picker" /></div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{COLS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Linked goal</Label>
            <Select value={f.goal_id || "none"} onValueChange={(v) => setF({ ...f, goal_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="task-goal-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">No goal</SelectItem>{goals.map((g) => <SelectItem key={g.goal_id} value={g.goal_id}>{g.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            {editing && <Button type="button" variant="ghost" className="text-destructive mr-auto" onClick={remove} data-testid="task-delete-button"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>}
            <Button type="submit" disabled={busy} data-testid="task-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}{editing ? "Save" : "Create task"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const [params, setParams] = useSearchParams();
  const { employees } = useEmployees();
  const [tasks, setTasks] = useState(null);
  const [goals, setGoals] = useState([]);
  const [who, setWho] = useState("all");
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const today = localToday();

  const load = useCallback(async () => {
    try {
      const [t, g] = await Promise.all([api.get("/tasks"), api.get("/goals")]);
      setTasks(t.data);
      setGoals(g.data);
    } catch (e) { toast.error(errMsg(e)); setTasks([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (params.get("new")) { setDialog({ open: true, initial: null }); setParams({}, { replace: true }); } }, [params, setParams]);

  const move = async (task, status) => {
    if (task.status === status) return;
    setTasks((ts) => ts.map((t) => (t.task_id === task.task_id ? { ...t, status } : t)));
    try { await api.patch(`/tasks/${task.task_id}`, { status }); } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const onDragEnd = (r) => {
    if (!r.destination) return;
    const t = tasks.find((x) => x.task_id === r.draggableId);
    if (t) move(t, r.destination.droppableId);
  };

  const visible = (tasks || []).filter((t) => who === "all" || (who === "none" ? !t.assignee_id : t.assignee_id === who));
  const goalName = (id) => goals.find((g) => g.goal_id === id)?.title;

  return (
    <div data-testid="tasks-page">
      <PageHeader title="Tasks" subtitle="Drag cards between columns or change status inside a card"
        actions={<>
          <Select value={who} onValueChange={setWho}>
            <SelectTrigger className="w-48 bg-card" data-testid="tasks-assignee-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Everyone</SelectItem><SelectItem value="none">Unassigned</SelectItem>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => setDialog({ open: true, initial: null })} data-testid="kanban-add-task-button"><Plus className="h-4 w-4 mr-1.5" />New task</Button>
        </>} />
      {tasks === null ? <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {COLS.map((col) => {
              const list = visible.filter((t) => t.status === col.id);
              return (
                <Droppable droppableId={col.id} key={col.id}>
                  {(prov, snap) => (
                    <div ref={prov.innerRef} {...prov.droppableProps} data-testid={`kanban-column-${col.id}`}
                      className={`rounded-xl border border-border p-3 min-h-[200px] transition-colors ${snap.isDraggingOver ? "bg-accent" : "bg-muted"}`}>
                      <div className="flex items-center justify-between px-1 mb-3">
                        <p className="text-sm font-semibold">{col.label}</p>
                        <span className="text-xs bg-card rounded-full px-2 py-0.5 tabular-nums" data-testid={`kanban-count-${col.id}`}>{list.length}</span>
                      </div>
                      <div className="space-y-2">
                        {list.map((t, idx) => {
                          const overdue = t.due_date && t.due_date < today && t.status !== "done";
                          return (
                            <Draggable draggableId={t.task_id} index={idx} key={t.task_id}>
                              {(p, s) => (
                                <div ref={p.innerRef} {...p.draggableProps} data-testid="kanban-task-card"
                                  className={`bg-card border border-border rounded-lg p-3 ${s.isDragging ? "shadow-lg" : "shadow-sm hover:shadow-md"} transition-shadow`}>
                                  <div className="flex items-start gap-2">
                                    <span {...p.dragHandleProps} className="mt-0.5 text-muted-foreground cursor-grab" aria-label="Drag"><GripVertical className="h-4 w-4" /></span>
                                    <button className="text-left flex-1 min-w-0" onClick={() => setDialog({ open: true, initial: t })} data-testid="kanban-task-open">
                                      <p className={`text-sm font-medium leading-snug ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</p>
                                      {t.goal_id && goalName(t.goal_id) && <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1 truncate"><Target className="h-3 w-3" />{goalName(t.goal_id)}</p>}
                                    </button>
                                  </div>
                                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                                    <PersonAvatar name={t.assignee_name || "?"} className="h-6 w-6" />
                                    <PriorityDot priority={t.priority} />
                                    {t.due_date && <span className={`text-[11px] flex items-center gap-1 ${overdue ? "text-destructive font-medium" : "text-muted-foreground"}`}><CalendarClock className="h-3 w-3" />{fmtDate(t.due_date)}</span>}
                                    <Select value={t.status} onValueChange={(v) => move(t, v)}>
                                      <SelectTrigger className="h-7 w-[118px] ml-auto text-xs" data-testid="kanban-task-status-select"><SelectValue /></SelectTrigger>
                                      <SelectContent>{COLS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {prov.placeholder}
                        {list.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No tasks</p>}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
      )}
      <TaskDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} initial={dialog.initial} employees={employees} goals={goals} onSaved={load} />
    </div>
  );
}
